import React from 'react'
import PropTypes from 'prop-types'
import {Box} from '@chakra-ui/react3'
import {isEqual} from 'lodash'
import Image from '../../components/cms/image'
import {useStyles} from './context'
import {hasValidImageSrc, isVideoAsset} from '../../utils/utils'
import {getBrightcoveVideoProps} from '../../../utils/brightcove'
import DirectVideo from '../../components/cms/direct-video'
import useOcapiSite from '../../../hooks/useOcapiSite'
import {useBrightcoveNativeVideoEnabled} from '../../../hooks/use-brightcove-native-video-enabled'

/**
 * Hero asset component to display image or video
 *
 * @component
 * @param {object} [normalizedMobile] - Normalized mobile asset (editor-provided fields only)
 * @param {object} [normalizedDesktop] - Normalized desktop asset (editor-provided fields only)
 * @param {object|function} [videoRef] - Ref object or callback for the video element
 * @param {Function} [mobileOnError] - Callback when the mobile video fails to load
 * @param {Function} [desktopOnError] - Callback when the desktop video fails to load
 * @returns {JSX.Element|null} Rendered asset or null if no asset provided
 */
const HeroAsset = ({
    normalizedMobile,
    normalizedDesktop,
    mobileVideoRef,
    desktopVideoRef,
    mobileOnError,
    desktopOnError
}) => {
    const styles = useStyles()
    const nativeBrightcove = useBrightcoveNativeVideoEnabled()
    const {accountId} = useOcapiSite()

    if (!normalizedMobile && !normalizedDesktop) return null

    const renderAssetContent = (currentAsset, ref, onError) => {
        if (!currentAsset) return null

        const {type: assetType, ...assetProps} = currentAsset

        if (isVideoAsset(currentAsset)) {
            // display direct videos, and brightcove ones only as native video
            if (currentAsset.type === 'brightcove' && nativeBrightcove) {
                const {brightcoveId, poster, title} = currentAsset
                return (
                    <DirectVideo
                        // a different video needs a fresh <video>, which ignores <source> changes
                        key={`${accountId}/${brightcoveId}`}
                        ref={ref}
                        {...assetProps}
                        {...getBrightcoveVideoProps(accountId, brightcoveId, poster, title)}
                        loading="eager"
                        objectFit="cover"
                        alwaysMuted
                        muted
                        videoControls={false}
                        widths={{base: '100vw'}}
                        onError={onError}
                    />
                )
            }

            if (currentAsset.type === 'direct') {
                return (
                    <DirectVideo
                        ref={ref}
                        {...currentAsset}
                        loading="eager"
                        objectFit="cover"
                        alwaysMuted
                        muted
                        videoControls={false}
                        widths={{base: '100vw'}}
                        onError={onError}
                    />
                )
            }

            // fallback to display the poster if available
            if (hasValidImageSrc(currentAsset.poster)) {
                return (
                    <Image
                        {...currentAsset.poster}
                        widths={{base: '100vw'}}
                        loading="eager"
                        objectFit="cover"
                        preload
                        // fetchpriority="high" optimizes LCP (Largest Contentful Paint) by prioritizing
                        // hero image download over other resources. Combined with loading="eager" and
                        // preload, this ensures fastest possible above-the-fold render.
                        fetchpriority="high"
                    />
                )
            }
        }

        // For image assets, check if src is valid
        if (!hasValidImageSrc(currentAsset)) return null

        return (
            <Image
                {...assetProps}
                widths={{base: '100vw'}}
                loading="eager"
                objectFit="cover"
                preload
                // fetchpriority="high" optimizes LCP (Largest Contentful Paint) by prioritizing
                // hero image download over other resources. Combined with loading="eager" and
                // preload, this ensures fastest possible above-the-fold render.
                fetchpriority="high"
            />
        )
    }

    // If only one asset is provided or both are the same after normalization, render once
    if (!normalizedMobile || !normalizedDesktop || isEqual(normalizedMobile, normalizedDesktop)) {
        const singleAsset = normalizedMobile || normalizedDesktop
        return (
            <Box css={styles.assetContainer}>
                {renderAssetContent(singleAsset, mobileVideoRef, mobileOnError)}
                {/* Darken Layer Left */}
                <Box css={styles.darkenLayerLeft} />
                {/* Darken Layer Bottom */}
                <Box css={styles.darkenLayerBottom} />
            </Box>
        )
    }

    // Different assets for mobile/desktop - render both with display toggles
    return (
        <Box css={styles.assetContainer}>
            {/* Mobile asset */}
            <Box display={{base: 'block', s: 'none'}} height="100%">
                {renderAssetContent(normalizedMobile, mobileVideoRef, mobileOnError)}
            </Box>
            {/* Desktop asset */}
            <Box display={{base: 'none', s: 'block'}} height="100%">
                {renderAssetContent(normalizedDesktop, desktopVideoRef, desktopOnError)}
            </Box>
            {/* Darken Layer Left */}
            <Box css={styles.darkenLayerLeft} />
            {/* Darken Layer Bottom */}
            <Box css={styles.darkenLayerBottom} />
        </Box>
    )
}

HeroAsset.propTypes = {
    normalizedMobile: PropTypes.object,
    normalizedDesktop: PropTypes.object,
    mobileVideoRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    desktopVideoRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    mobileOnError: PropTypes.func,
    desktopOnError: PropTypes.func
}

export default HeroAsset
